/**
 * Design-system primitives. Class names map to styles/ui.css.
 */
import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type DependencyList,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import type { DataQuality, Freshness } from '@apos/shared';
import { Icon, type IconName } from './Icon';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

// iOS Safari / WKWebView only applies :active (our pressed feedback) when a touchstart listener exists.
if (typeof document !== 'undefined') document.addEventListener('touchstart', () => undefined, { passive: true });
export { cx };

/**
 * Measures the active child of a container so a sliding indicator (Segmented thumb, Tabs underline)
 * can follow it. Returns null until measured (and in jsdom), so the static `.is-on` style shows first.
 */
export function useActiveIndicator(container: React.RefObject<HTMLElement | null>, activeSelector: string, deps: DependencyList) {
  const [box, setBox] = useState<{ x: number; w: number } | null>(null);
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;
    const measure = () => {
      const a = el.querySelector<HTMLElement>(activeSelector);
      const next = a && a.offsetWidth > 0 ? { x: a.offsetLeft, w: a.offsetWidth } : null;
      setBox((prev) => (prev?.x === next?.x && prev?.w === next?.w ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return box;
}

// ───────────── Button ─────────────

/**
 * `link`: inline text action (underlined, no box) — use instead of one-off link buttons.
 * `danger`: outlined destructive action inside a form (Remove camera).
 * `destructive`: filled red, only for the final button of a destructive confirmation (End shoot).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive' | 'live' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconRight, loading, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  const iconSize = size === 'sm' ? 15 : size === 'xl' ? 22 : 18;
  return (
    <button
      ref={ref}
      type={type}
      className={cx('btn', `btn--${variant}`, `btn--${size}`, block && 'btn--block', loading && 'is-loading', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={iconSize - 2} /> : icon ? <Icon name={icon} size={iconSize} /> : null}
      {children !== undefined && children !== null && <span className="btn__label">{children}</span>}
      {iconRight && !loading ? <Icon name={iconRight} size={iconSize} /> : null}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Accessible label; also the tooltip. */
  label: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'ghost' | 'secondary' | 'primary' | 'overlay';
  active?: boolean;
  iconSize?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', variant = 'ghost', active, className, iconSize, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active === undefined ? undefined : active}
      className={cx('icon-btn', `icon-btn--${size}`, `icon-btn--${variant}`, active && 'is-active', className)}
      {...rest}
    >
      <Icon name={icon} size={iconSize ?? (size === 'sm' ? 16 : size === 'lg' ? 22 : 19)} />
    </button>
  );
});

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg className="spinner" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity=".25" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ───────────── Chip / Badge ─────────────

export type ChipTone =
  | 'neutral'
  | 'strong'
  | 'live'
  | 'aging'
  | 'stale'
  | 'lost'
  | 'alert'
  | 'sun'
  | 'estimated'
  | 'predicted'
  | 'observed'
  | 'interpolated'
  | 'manual'
  | 'photo'
  /** Brand punctuation (you / selected filter). Never a status. */
  | 'accent'
  /** Semantic tones (DESIGN_SYSTEM.md "Colour semantics"). */
  | 'critical'
  | 'attention'
  | 'warning'
  | 'info'
  /** Not started / no position yet / not configured: neutral, dashed. Never red. */
  | 'idle'
  /** Production call (Engines start, Rolling): ink outline. */
  | 'call'
  /** Recording / rolling tally (raspberry, distinct from alert red). Pair with `dot`. */
  | 'rec'
  | 'hero'
  | 'missed';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ChipTone;
  /** Leading status dot (pulses when tone = live). */
  dot?: boolean;
  icon?: IconName;
  size?: 'sm' | 'md';
}

export function Chip({ tone = 'neutral', dot, icon, size = 'md', className, children, ...rest }: ChipProps) {
  return (
    <span className={cx('chip', `chip--${tone}`, `chip--${size}`, className)} {...rest}>
      {dot ? <span className="chip__dot" aria-hidden /> : null}
      {icon ? <Icon name={icon} size={size === 'sm' ? 11 : 13} /> : null}
      <span className="chip__label">{children}</span>
    </span>
  );
}
export const Badge = Chip;

/** Small numeric badge (unread, alert count). Pins to the top-right of a `position: relative` parent with `floating`. */
export function CountBadge({ count, max = 99, label, floating, tone = 'strong', className }: { count: number; max?: number; label?: string; floating?: boolean; tone?: 'strong' | 'alert' | 'accent'; className?: string }) {
  if (count <= 0) return null;
  return (
    <span className={cx('count-badge', `count-badge--${tone}`, floating && 'count-badge--floating', 'num', className)} aria-label={label}>
      {count > max ? `${max}+` : count}
    </span>
  );
}

const FRESH_LABEL: Record<Freshness, string> = { live: 'LIVE', aging: 'AGING', stale: 'STALE', lost: 'LOST', none: 'NO DATA' };
export function FreshnessChip({ freshness, ageText, size = 'sm' }: { freshness: Freshness; ageText?: string; size?: 'sm' | 'md' }) {
  const tone: ChipTone = freshness === 'none' ? 'neutral' : freshness;
  return (
    <Chip tone={tone} dot size={size} title={ageText}>
      {FRESH_LABEL[freshness]}
      {ageText && freshness !== 'live' ? <span className="chip__meta">{ageText}</span> : null}
    </Chip>
  );
}

const QUALITY_LABEL: Record<DataQuality, string> = {
  observed: 'OBS',
  interpolated: 'INTERP',
  estimated: 'EST',
  predicted: 'PRED',
  manual: 'MANUAL',
};
export function QualityChip({ quality, size = 'sm' }: { quality: DataQuality; size?: 'sm' | 'md' }) {
  return (
    <Chip tone={quality} size={size} title={quality}>
      {QUALITY_LABEL[quality]}
    </Chip>
  );
}

// ───────────── Panel ─────────────

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  /** Small label above the title, e.g. "SELECTED". */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  padded?: boolean;
  /** `glass` (= `overlay`): translucent, blurred — for panels floating over the map. */
  variant?: 'raised' | 'flat' | 'overlay' | 'glass';
}

export function Panel({ title, eyebrow, actions, footer, padded = true, variant = 'raised', className, children, ...rest }: PanelProps) {
  return (
    <section className={cx('panel', `panel--${variant}`, className)} {...rest}>
      {(title || actions || eyebrow) && (
        <header className="panel__head">
          <div className="panel__titles">
            {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
            {title ? <h2 className="panel__title">{title}</h2> : null}
          </div>
          {actions ? <div className="panel__actions">{actions}</div> : null}
        </header>
      )}
      <div className={cx('panel__body', padded && 'panel__body--padded')}>{children}</div>
      {footer ? <footer className="panel__foot">{footer}</footer> : null}
    </section>
  );
}

// ───────────── Readout ─────────────

export interface Provenance {
  quality?: DataQuality;
  freshness?: Freshness;
  /** preformatted age, e.g. "12 s ago" */
  age?: string;
  /** GPS accuracy (m) */
  accuracyM?: number | null;
  source?: string;
  note?: string;
}

export interface ReadoutProps {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  provenance?: Provenance | string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Grey out as stale. */
  dim?: boolean;
  tone?: 'default' | 'live' | 'aging' | 'stale' | 'lost' | 'sun' | 'photo';
  align?: 'start' | 'end';
  className?: string;
}

export function provenanceText(p: Provenance | string | undefined): string | null {
  if (!p) return null;
  if (typeof p === 'string') return p;
  const parts: string[] = [];
  if (p.quality) parts.push(p.quality);
  if (p.age) parts.push(p.age);
  if (p.accuracyM !== undefined && p.accuracyM !== null) parts.push(`±${Math.round(p.accuracyM)} m`);
  if (p.source) parts.push(p.source);
  if (p.note) parts.push(p.note);
  return parts.join(' · ') || null;
}

export function Readout({ label, value, unit, provenance, size = 'md', dim, tone = 'default', align = 'start', className }: ReadoutProps) {
  const prov = provenanceText(provenance);
  const stale = typeof provenance === 'object' && (provenance.freshness === 'stale' || provenance.freshness === 'lost');
  const estimated = typeof provenance === 'object' && (provenance.quality === 'estimated' || provenance.quality === 'predicted');
  return (
    <div className={cx('readout', `readout--${size}`, `readout--${tone}`, `readout--${align}`, (dim || stale) && 'is-dim', estimated && 'is-estimated', className)}>
      <div className="readout__label">{label}</div>
      <div className="readout__value">
        <span className="num">{value}</span>
        {unit ? <span className="readout__unit">{unit}</span> : null}
      </div>
      {prov ? <div className="readout__prov">{prov}</div> : null}
    </div>
  );
}

// ───────────── Segmented ─────────────

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  disabled?: boolean;
  title?: string;
}

export interface SegmentedProps<T extends string> {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange(v: T): void;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  label?: string;
  className?: string;
}

export function Segmented<T extends string>({ options, value, onChange, size = 'md', block, label, className }: SegmentedProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const thumb = useActiveIndicator(ref, '.segmented__opt.is-on', [value, options.length, size]);
  return (
    <div ref={ref} role="radiogroup" aria-label={label} className={cx('segmented', `segmented--${size}`, block && 'segmented--block', thumb && 'has-thumb', className)}>
      {thumb ? <span className="segmented__thumb" aria-hidden style={{ width: thumb.w, transform: `translateX(${thumb.x}px)` }} /> : null}
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          title={o.title}
          className={cx('segmented__opt', o.value === value && 'is-on')}
          onClick={() => onChange(o.value)}
        >
          {o.icon ? <Icon name={o.icon} size={size === 'lg' ? 18 : 15} /> : null}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// ───────────── Tabs ─────────────

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  badge?: ReactNode;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
}: {
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange(v: T): void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bar = useActiveIndicator(ref, '.tabs__tab.is-on', [value, items.length]);
  return (
    <div ref={ref} role="tablist" className={cx('tabs', bar && 'has-indicator', className)}>
      {items.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          aria-selected={t.id === value}
          className={cx('tabs__tab', t.id === value && 'is-on')}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.badge !== undefined && t.badge !== null ? <span className="tabs__badge num">{t.badge}</span> : null}
        </button>
      ))}
      {bar ? <span className="tabs__indicator" aria-hidden style={{ width: Math.max(0, bar.w - 16), transform: `translateX(${bar.x + 8}px)` }} /> : null}
    </div>
  );
}

// ───────────── Fields ─────────────

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
  optional?: boolean;
}

/** `<Field label="Name">{(id) => <TextInput id={id} … />}</Field>` */
export function Field({ label, hint, error, children, className, optional }: FieldProps) {
  const id = useId();
  return (
    <div className={cx('field', error ? 'has-error' : false, className)}>
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? <span className="field__optional">optional</span> : null}
      </label>
      {children(id)}
      {error ? <div className="field__error" role="alert">{error}</div> : hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; icon?: IconName }>(
  function TextInput({ className, mono, icon, ...rest }, ref) {
    if (icon) {
      return (
        <div className="input-wrap">
          <Icon name={icon} size={16} className="input-wrap__icon" />
          <input ref={ref} className={cx('input', 'input--with-icon', mono && 'input--mono', className)} {...rest} />
        </div>
      );
    }
    return <input ref={ref} className={cx('input', mono && 'input--mono', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <div className="select-wrap">
      <select ref={ref} className={cx('input', 'select', className)} {...rest}>
        {children}
      </select>
      <Icon name="chevron-down" size={16} className="select-wrap__chev" />
    </div>
  );
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx('input', 'textarea', className)} {...rest} />;
});

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange(v: boolean): void; label: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx('toggle', disabled && 'is-disabled')}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle__track" aria-hidden>
        <span className="toggle__thumb" />
      </span>
      <span className="toggle__label">{label}</span>
    </label>
  );
}

/** Checkbox for forms and lists (Toggle is for settings that take effect immediately). */
export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  className,
  name,
  ariaLabel,
}: {
  checked: boolean;
  onChange(v: boolean): void;
  label: ReactNode;
  disabled?: boolean;
  className?: string;
  name?: string;
  /** Accessible name when the visible label is not text (or is visually hidden). */
  ariaLabel?: string;
}) {
  return (
    <label className={cx('checkbox', disabled && 'is-disabled', className)}>
      <input type="checkbox" name={name} aria-label={ariaLabel} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="checkbox__box" aria-hidden>
        <Icon name="check" size={14} />
      </span>
      <span className="checkbox__label">{label}</span>
    </label>
  );
}

// ───────────── EmptyState / Disclaimer / misc ─────────────

export function EmptyState({ icon, title, body, action, className }: { icon?: IconName; title: ReactNode; body?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cx('empty', className)}>
      {icon ? (
        <div className="empty__icon">
          <Icon name={icon} size={22} />
        </div>
      ) : null}
      <div className="empty__title">{title}</div>
      {body ? <div className="empty__body">{body}</div> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}

export const DISCLAIMER_TEXT = 'Content situational awareness only — not for navigation, traffic separation, or flight-safety decisions.';

/** Persistent footer on LIVE and A2A (contract design standard). */
export function Disclaimer({ className, compact }: { className?: string; compact?: boolean }) {
  return (
    <div className={cx('disclaimer', compact && 'disclaimer--compact', className)} role="note">
      <Icon name="info" size={13} />
      <span>{DISCLAIMER_TEXT}</span>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Divider({ vertical }: { vertical?: boolean }) {
  return <span className={cx('divider', vertical && 'divider--v')} aria-hidden />;
}

/** Dense key/value table used in panels. */
export function KeyValue({ rows, className }: { rows: Array<[ReactNode, ReactNode]>; className?: string }) {
  return (
    <dl className={cx('kv', className)}>
      {rows.map(([k, v], i) => (
        <div className="kv__row" key={i}>
          <dt>{k}</dt>
          <dd className="num">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
